import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Mail, User, Eye, EyeOff, Shield, Smartphone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const { user, login, checkAuth } = useAuth();
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');

  const [mode, setMode] = useState(inviteCode ? 'signup' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);   // Iter 155 — 30-day cookie
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA states
  const [needs2FA, setNeeds2FA] = useState(false);
  const [needs2FASetup, setNeeds2FASetup] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [manualSecret, setManualSecret] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);

  const [inviteInfo, setInviteInfo] = useState(null);

  useEffect(() => { if (user) navigate('/'); }, [user, navigate]);
  useEffect(() => {
    if (inviteCode) {
      axios.get(`${API}/api/users/invite/${inviteCode}`).then(r => { setInviteInfo(r.data); setName(r.data.name); setEmail(r.data.email); }).catch(() => setError('Invalid invite link'));
    }
  }, [inviteCode]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      if (mode === 'signup') {
        let res;
        if (inviteCode) {
          res = await axios.post(`${API}/api/auth/signup-invite?code=${inviteCode}`, { email, password, name }, { withCredentials: true });
        } else {
          res = await axios.post(`${API}/api/auth/signup`, { email, password, name }, { withCredentials: true });
        }
        const data = res.data;
        if (data.requires_2fa_setup) {
          setNeeds2FASetup(true); setQrCode(data.qr_code); setManualSecret(data.secret_manual); setTempToken(data.temp_token);
        } else {
          await checkAuth(); navigate('/');
        }
      } else {
        const res = await axios.post(`${API}/api/auth/login`, { email, password, remember_me: rememberMe }, { withCredentials: true });
        const data = res.data;
        if (data.status === 'requires_totp') {
          setNeeds2FA(true); setTempToken(data.temp_token);
        } else if (data.status === 'requires_2fa_setup') {
          setNeeds2FASetup(true); setQrCode(data.qr_code); setManualSecret(data.secret_manual); setTempToken(data.temp_token);
        } else {
          await checkAuth(); navigate('/');
        }
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : Array.isArray(d) ? d.map(e => e.msg).join(' ') : 'Something went wrong');
    }
    setLoading(false);
  };

  const handleTOTPVerify = async () => {
    setError(''); setLoading(true);
    try {
      const endpoint = needs2FASetup ? '/api/auth/verify-2fa-setup' : '/api/auth/verify-totp';
      const body = { totp_code: totpCode, temp_token: tempToken };
      if (!needs2FASetup) body.trust_device = trustDevice;
      await axios.post(`${API}${endpoint}`, body, { withCredentials: true });
      await checkAuth(); navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code');
    }
    setLoading(false);
  };

  // 2FA Setup Screen
  if (needs2FASetup) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="2fa-setup">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-accent flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8" style={{ color: 'var(--i3-teal)' }} />
            </div>
            <h1 className="font-['Outfit'] text-2xl font-bold text-foreground">Set Up 2FA</h1>
            <p className="text-sm text-muted-foreground mt-2">All i3 accounts require two-factor authentication. Scan this QR code with Google Authenticator or Authy.</p>
          </div>
          <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
            {qrCode && <img src={qrCode} alt="QR Code" className="mx-auto w-48 h-48 rounded-lg" data-testid="2fa-qr" />}
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">Or enter this code manually:</p>
              <code className="text-sm font-mono bg-muted px-3 py-1.5 rounded-lg text-foreground select-all" data-testid="2fa-manual-secret">{manualSecret}</code>
            </div>
            {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-500" data-testid="2fa-error">{error}</div>}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Enter 6-digit code from your authenticator</label>
              <div className="flex gap-2">
                <input data-testid="2fa-code-input" type="text" value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" maxLength={6} autoFocus
                  className="flex-1 px-4 py-3 text-center text-lg font-mono tracking-[0.3em] bg-muted border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#42CDD7]/30 focus:border-[#42CDD7]" />
                <button onClick={handleTOTPVerify} disabled={loading || totpCode.length !== 6} data-testid="2fa-verify-btn"
                  className="px-6 py-3 text-sm font-semibold text-white rounded-xl transition-colors disabled:opacity-40"
                  style={{ backgroundColor: '#42CDD7' }}>Verify</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // TOTP Login Screen
  if (needs2FA) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="totp-login">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-accent flex items-center justify-center mx-auto mb-4">
              <Smartphone className="w-8 h-8" style={{ color: 'var(--i3-teal)' }} />
            </div>
            <h1 className="font-['Outfit'] text-2xl font-bold text-foreground">Two-Factor Authentication</h1>
            <p className="text-sm text-muted-foreground mt-2">Enter the 6-digit code from your authenticator app</p>
          </div>
          <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
            {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-500" data-testid="totp-error">{error}</div>}
            <div className="flex gap-2">
              <input data-testid="totp-code-input" type="text" value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000" maxLength={6} autoFocus
                className="flex-1 px-4 py-3.5 text-center text-xl font-mono tracking-[0.4em] bg-muted border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#42CDD7]/30 focus:border-[#42CDD7]" />
              <button onClick={handleTOTPVerify} disabled={loading || totpCode.length !== 6} data-testid="totp-verify-btn"
                className="px-6 py-3.5 text-sm font-semibold text-white rounded-xl transition-colors disabled:opacity-40"
                style={{ backgroundColor: '#42CDD7' }}>Verify</button>
            </div>
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={trustDevice} onChange={e => setTrustDevice(e.target.checked)} data-testid="trust-device-checkbox"
                className="w-4 h-4 cursor-pointer" style={{ accentColor: '#42CDD7' }} />
              <span>Trust this device for 30 days (skip 2FA on this browser)</span>
            </label>
            <button onClick={() => { setNeeds2FA(false); setTotpCode(''); setTempToken(''); setTrustDevice(false); }} className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors">
              Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Login / Signup Form
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={dark ? "/brand/i3-logo-reverse.svg" : "/brand/i3-logo-stacked.png"}
            alt="i3 Radiology" className={dark ? "h-14 w-auto object-contain mx-auto mb-4" : "h-20 w-auto object-contain mx-auto mb-4"} />
          <h1 className="font-['Outfit'] text-2xl font-bold text-foreground" data-testid="login-title">
            {mode === 'login' ? 'Sign In' : inviteCode ? 'Accept Invitation' : 'Create Account'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {mode === 'login' ? 'Access i3 Radiology Assistant' : inviteCode ? `You've been invited as ${inviteInfo?.role || 'staff'}` : 'First account becomes Admin with 2FA'}
          </p>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-500" data-testid="login-error">{error}</div>}

            {mode === 'signup' && (
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1.5">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input data-testid="signup-name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" required
                    className="w-full pl-10 pr-4 py-3 text-sm bg-muted border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#42CDD7]/30 focus:border-[#42CDD7]" />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@practice.com.au" required
                  className="w-full pl-10 pr-4 py-3 text-sm bg-muted border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#42CDD7]/30 focus:border-[#42CDD7]" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input data-testid="login-password" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" required
                  className="w-full pl-10 pr-10 py-3 text-sm bg-muted border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#42CDD7]/30 focus:border-[#42CDD7]" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {mode === 'login' && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none pt-1"
                     data-testid="remember-me-wrapper">
                <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)}
                  data-testid="remember-me"
                  className="w-4 h-4 accent-[#42CDD7] cursor-pointer" />
                <span>Keep me signed in for 30 days on this device</span>
              </label>
            )}

            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full py-3 text-sm font-semibold text-white rounded-xl transition-colors disabled:opacity-40"
              style={{ backgroundColor: '#42CDD7' }}>
              {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </button>

            {mode === 'login' && (
              <div className="text-center pt-1">
                <Link to="/forgot-password" data-testid="forgot-pw-link" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                  Forgot password?
                </Link>
              </div>
            )}
          </form>

          {!inviteCode && (
            <div className="mt-4 text-center">
              <button onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(''); }} data-testid="toggle-mode"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-[10px] text-muted-foreground mt-4">i3 Radiology — Infinity Imaging & Interventions</p>
      </div>
    </div>
  );
}
